import React, { useEffect, useState } from 'react';
import { alpha, Box, Button, Chip, Stack, Theme, Typography } from '@mui/material';
import { ElementInconsistency } from 'api/element-issues';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import IssueCard, { IssueCardRows } from 'components/common/issue-card/IssueCard';
import useForceExpand from 'hooks/useForceExpand';

export type IssueObjectType = 'report' | 'dataset' | 'domain';

const SEVERITY_COLOR_ALIAS = {
    High: 'error',
    Medium: 'warning',
    Low: 'default',
};

function pluralize(count: number, singular: string, plural: string): string {
    return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

interface Props {
    inconsistencies: ElementInconsistency[];
    compact?: boolean;
    // Auto-expand on gating errors (§2.2): re-expands whenever the token changes
    forceExpandToken?: number;
    objectType?: IssueObjectType;
    // Manage Knowledge Graph (MI-29733 §5.2): without it the Resolve links and Review-all action
    // are hidden while the rest of the card still renders (spec §4.1)
    canResolve?: boolean;
}

export default function InconsistenciesCard({
    inconsistencies,
    compact,
    forceExpandToken,
    objectType = 'report',
    canResolve = false,
}: Props) {
    const { t } = useBundleTranslation('app/extreport/concierge');
    // Cards that mount already resolved start collapsed — no expand-then-collapse flicker
    const [expanded, setExpanded] = useState(() => inconsistencies.length > 0);

    useForceExpand(forceExpandToken, setExpanded);

    const allResolved = inconsistencies.length === 0;

    // Auto-collapse to `Resolved` the moment the outstanding count hits zero (spec §4.4)
    useEffect(() => {
        if (allResolved) setExpanded(false);
    }, [allResolved]);

    const handleRowActivate = (resolveUrl: string) => {
        if (!canResolve) return;
        window.open(resolveUrl, '_blank', 'noopener');
    };

    const reviewAllUrl = (() => {
        // The Measure Conflicts editor accepts one model filter — scope only when every row agrees
        const modelUids = [...new Set(inconsistencies.map((i) => i.modelUid).filter(Boolean))];
        const modelUid = modelUids.length === 1 ? (modelUids[0] as string) : null;
        return `/editor/measure-conflicts${modelUid ? `?model=${encodeURIComponent(modelUid)}` : ''}`;
    })();

    const badge = allResolved ? (
        <Chip label={t('resolved_badge')} color="success" className={'chip-mod--round-and-text'} />
    ) : (
        <Chip
            label={t('unresolved_count', { count: inconsistencies.length })}
            color="error"
            className={'chip-mod--round-and-text'}
        />
    );

    return (
        <IssueCard
            color="warning"
            title={t('title')}
            badge={badge}
            expanded={expanded}
            onToggle={() => setExpanded((prev) => !prev)}
        >
            <Box sx={{ pb: 1, fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1) }}>
                {t('description', { objectType: t(`object_type_${objectType}`) })}
            </Box>

            <IssueCardRows>
                {inconsistencies.map((item, idx) => (
                    <Stack
                        key={item.measureId}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        tabIndex={canResolve ? 0 : -1}
                        role={canResolve ? 'link' : undefined}
                        onClick={() => handleRowActivate(item.resolveUrl)}
                        onKeyDown={(e) => {
                            if (canResolve && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleRowActivate(item.resolveUrl);
                            }
                        }}
                        sx={{
                            px: 1,
                            py: '6px',
                            cursor: canResolve ? 'pointer' : 'default',
                            borderTop: idx > 0 ? '1px solid' : 'none',
                            borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                            '&:hover': canResolve
                                ? {
                                      backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
                                      '& .resolve-link': { textDecoration: 'underline' },
                                  }
                                : undefined,
                        }}
                    >
                        <Stack sx={{ flexGrow: 1 }}>
                            <Box>{item.measureName}</Box>
                            {!compact && (
                                <Typography sx={{ color: 'text.secondary', mt: 0.25 }}>
                                    {t('inconsistent_with')}{' '}
                                    <strong>{pluralize(item.modelCount, item.modelType, item.modelType + 's')}</strong>,{' '}
                                    {t('used_by')} <strong>{t('report_count', { count: item.reportCount })}</strong>
                                </Typography>
                            )}
                        </Stack>

                        <Chip
                            label={t('resolved_badge')}
                            color={SEVERITY_COLOR_ALIAS[item.severity] as any}
                            className={'chip-mod--round-and-text'}
                        />

                        {canResolve && (
                            <Box
                                className="resolve-link"
                                component="span"
                                sx={{
                                    color: 'primary.main',
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                {t('resolve')}
                                <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                            </Box>
                        )}
                    </Stack>
                ))}
            </IssueCardRows>

            {canResolve && !allResolved && (
                <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => window.open(reviewAllUrl, '_blank', 'noopener')}
                        endIcon={<IconMi icon="arrow-right" />}
                        data-test="inconsistencies_review_all"
                    >
                        {t('review_all')}
                    </Button>
                </Stack>
            )}
        </IssueCard>
    );
}
